import React, { useEffect, useState, useCallback } from "react";
import { AlertTriangle, X, MailWarning, CalendarClock } from "lucide-react";
import api from "@/lib/api";

/**
 * DashboardAlerts — persistent, dismissible top-of-dashboard banner strip.
 *
 * Right now surfaces two production-critical states:
 *   • Google Calendar needs_reconnect (dead refresh token)
 *   • Email address is on the hard-bounce suppression list
 *
 * All CTAs jump the user straight to Settings so they can resolve without
 * asking support. Each banner is dismiss-scoped to the current tab session.
 */
export default function DashboardAlerts({ onGoSettings }) {
  const [gcalNeedsReconnect, setGcalNeedsReconnect] = useState(false);
  const [emailSuppressed, setEmailSuppressed] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [reconnecting, setReconnecting] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("dash-alerts-dismissed") || "[]"); } catch { return []; }
  });

  const dismiss = useCallback((key) => {
    setDismissed((prev) => {
      const next = Array.from(new Set([...prev, key]));
      sessionStorage.setItem("dash-alerts-dismissed", JSON.stringify(next));
      return next;
    });
  }, []);

  const reconnectCalendar = useCallback(async () => {
    setReconnecting(true);
    try {
      const { data } = await api.get("/oauth/calendar/login");
      // One-tap deep link — go straight to Google, skip the Settings tab.
      window.location.href = data.authorization_url;
    } catch {
      setReconnecting(false);
      onGoSettings && onGoSettings();
    }
  }, [onGoSettings]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/oauth/calendar/status");
        if (!cancel) setGcalNeedsReconnect(!!data?.needs_reconnect);
      } catch { /* silent — banner just won't show */ }
      try {
        const me = await api.get("/auth/me");
        const email = me.data?.user?.email || me.data?.email || "";
        if (!cancel) setUserEmail(email);
        if (email) {
          const supp = await api.get(`/emails/suppressions/check?email=${encodeURIComponent(email)}`);
          if (!cancel) setEmailSuppressed(!!supp.data?.suppressed);
        }
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, []);

  const showGcal = gcalNeedsReconnect && !dismissed.includes("gcal");
  const showEmail = emailSuppressed && !dismissed.includes("email");
  if (!showGcal && !showEmail) return null;

  return (
    <div className="space-y-3 mb-6" data-testid="dashboard-alerts">
      {showGcal && (
        <Banner
          tone="amber"
          icon={CalendarClock}
          title="Your Google Calendar access expired."
          body="Google revoked our token, so I stopped writing your deadlines. Reconnect and I'll catch up on every renewal."
          cta={reconnecting ? "Redirecting to Google…" : "Reconnect Calendar"}
          ctaDisabled={reconnecting}
          onCta={reconnectCalendar}
          onDismiss={() => dismiss("gcal")}
          testid="alert-gcal-reconnect"
        />
      )}
      {showEmail && (
        <Banner
          tone="rose"
          icon={MailWarning}
          title="Your email address is bouncing."
          body={`I can't reach ${userEmail} anymore — your inbox rejected my messages. Update it in Settings and I'll auto-unblock delivery.`}
          cta="Fix email address"
          onCta={onGoSettings}
          onDismiss={() => dismiss("email")}
          testid="alert-email-suppressed"
        />
      )}
    </div>
  );
}

function Banner({ tone, icon: Icon, title, body, cta, onCta, onDismiss, testid, ctaDisabled }) {
  const toneCls = tone === "rose"
    ? "border-rose-400/40 bg-rose-500/10 text-rose-100"
    : "border-amber-400/40 bg-amber-500/10 text-amber-100";
  const btnCls = tone === "rose"
    ? "bg-rose-500 hover:bg-rose-400"
    : "bg-amber-500 hover:bg-amber-400";
  return (
    <div className={`rounded-2xl border ${toneCls} px-4 sm:px-5 py-4 flex items-start gap-3`} data-testid={testid}>
      <div className="shrink-0 mt-0.5"><Icon size={18} /></div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-white leading-snug flex items-center gap-2">
          <AlertTriangle size={14} className="opacity-80" /> {title}
        </div>
        <div className="text-white/80 text-sm mt-1">{body}</div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button
            onClick={onCta}
            disabled={ctaDisabled}
            className={`rounded-full ${btnCls} text-white font-bold px-4 py-2 min-h-[40px] text-sm transition-colors disabled:opacity-60`}
            data-testid={`${testid}-cta`}
          >
            {cta}
          </button>
          <button
            onClick={onDismiss}
            className="rounded-full border border-white/15 hover:border-white/30 text-white/80 px-4 py-2 min-h-[40px] text-sm"
            data-testid={`${testid}-dismiss`}
          >
            Later
          </button>
        </div>
      </div>
      <button onClick={onDismiss} className="text-white/40 hover:text-white/80 p-1 -m-1 min-h-[36px] min-w-[36px]" aria-label="Dismiss">
        <X size={16} />
      </button>
    </div>
  );
}
